/* Shared axios instance + 401 auto-refresh.
 *
 * Importing this module (anywhere, once) attaches a response interceptor to
 * BOTH the shared `api` instance and the global axios default instance, so
 * existing pages that use `axios` directly get auto-refresh for free.
 *
 * Interceptor behaviour:
 *  - Only acts on requests targeting our backend ('/api/...' or `${API_URL}/api/...`).
 *  - Never acts on /api/auth/refresh or /api/auth/login themselves.
 *  - On a 401: POST /api/auth/refresh once, then retry the original request once.
 *    Retried requests are flagged with `_retried` so a second 401 can't loop.
 *  - Concurrent 401s share a single in-flight refresh promise (no stampede).
 *  - If the refresh itself fails, the ORIGINAL error is rejected.
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to format API error details (e.g. error.response?.data?.detail)
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

// Resolve the request's path relative to our backend, or null if it targets
// some other origin.
function backendPath(config) {
  let url = config?.url || '';
  if (API_URL && url.startsWith(API_URL)) {
    url = url.slice(API_URL.length);
  }
  if (/^https?:\/\//i.test(url)) return null; // absolute URL to another origin
  if (!url.startsWith('/')) url = `/${url}`; // relative path via instance baseURL
  return url.startsWith('/api/') || url === '/api' ? url : null;
}

// Single in-flight refresh shared across all queued 401s.
let refreshPromise = null;

function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function attachAuthRefreshInterceptor(client) {
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;
      if (!config || config._retried || error?.response?.status !== 401) {
        return Promise.reject(error);
      }
      const path = backendPath(config);
      if (!path || path.startsWith('/api/auth/refresh') || path.startsWith('/api/auth/login')) {
        return Promise.reject(error);
      }
      try {
        await refreshSession();
      } catch (refreshError) {
        return Promise.reject(error); // refresh failed — surface the original error
      }
      config._retried = true;
      return client.request(config);
    }
  );
}

attachAuthRefreshInterceptor(api);
attachAuthRefreshInterceptor(axios);

export default api;
